import React, { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from "@mui/material";
import {
  CheckCircleRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  LockRounded,
} from "@mui/icons-material";
import DashboardLayout from "../components/DashboardLayout";
import RewardedProfileDisplay from "../components/RewardedProfileDisplay";
import avatarImage from "../assets/spr_placeholder_avatar.png";
import useCurrentUserData from "../hooks/useCurrentUserData";
import { AvatarConfig } from "../types/user";
import { dashboardPalette } from "../theme";
import {
  chunkRewards,
  EquippedRewardState,
  getAccessoryFromReward,
  getRewardBadgeVisualValue,
  isImageSource,
  isRewardEquipable,
  loadAvatarConfig,
  loadClaimedRewardIds,
  loadEquippedRewardState,
  NormalizedReward,
  normalizeRewardCollection,
  saveClaimedRewardIds,
  saveEquippedRewardState,
} from "../utils/rewards";
import { useAuth } from "../contexts/AuthContext";
import { UpdateEntryRewards } from "../firebase-config";


const LEVEL_STEP_XP = 120;
const REWARDS_PER_PAGE = 5;

const buildLevelThresholds = (rewardCount: number) =>
  Array.from(
    { length: Math.max(1, rewardCount) },
    (_, index) => index * LEVEL_STEP_XP,
  );

const getCurrentLevel = (xp: number, thresholds: number[]) => {
  let unlockedLevel = 1;

  thresholds.forEach((threshold, index) => {
    if (xp >= threshold) {
      unlockedLevel = index + 1;
    }
  });

  return Math.min(unlockedLevel, thresholds.length);
};

const getCurrentSegmentProgress = (
  xp: number,
  thresholds: number[],
  level: number,
) => {
  const currentThreshold = thresholds[level - 1] ?? 0;
  const nextThreshold = thresholds[level] ?? currentThreshold + LEVEL_STEP_XP;
  const totalSegment = nextThreshold - currentThreshold;

  if (totalSegment <= 0) {
    return 100;
  }

  return Math.min(
    100,
    Math.max(0, ((xp - currentThreshold) / totalSegment) * 100),
  );
};

const getTrackProgress = (
  level: number,
  segmentProgress: number,
  rewardCount: number,
) => {
  if (rewardCount <= 1) {
    return 100;
  }

  return Math.min(
    100,
    ((level - 1 + segmentProgress / 100) / (rewardCount - 1)) * 100,
  );
};

const getRewardPalette = (reward: NormalizedReward) => {
  switch (reward.tone) {
    case "teal":
      return {
        background: "linear-gradient(180deg, #ffe6b2 0%, #ffcf83 100%)",
        border: "#f1ba56",
        badgeBackground: "#b9eef2",
        badgeColor: "#2a8791",
        textColor: dashboardPalette.headerBlue,
      };
    case "sunset":
      return {
        background: "linear-gradient(180deg, #ffbb3b 0%, #ffce56 100%)",
        border: "#ec9d00",
        badgeBackground: "#ffd86b",
        badgeColor: dashboardPalette.headerBlue,
        textColor: dashboardPalette.headerBlue,
      };
    case "sky":
      return {
        background: "linear-gradient(180deg, #73d0ff 0%, #56b9f1 100%)",
        border: "#2f9bdc",
        badgeBackground: "rgba(255, 255, 255, 0.72)",
        badgeColor: dashboardPalette.headerBlue,
        textColor: "#ffffff",
      };
    default:
      return {
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(240,242,248,0.92) 100%)",
        border: "#d9deeb",
        badgeBackground: "rgba(255, 255, 255, 0.85)",
        badgeColor: "#9aa5c2",
        textColor: dashboardPalette.headerBlue,
      };
  }
};

const Redeem: React.FC = () => {
  const { userData } = useCurrentUserData();
  const [claimedRewards, setClaimedRewards] =
    useState<string[]>(loadClaimedRewardIds);
  const [avatarConfig, setAvatarConfig] =
    useState<AvatarConfig>(loadAvatarConfig);
  const [equippedRewards, setEquippedRewards] = useState<EquippedRewardState>(
    loadEquippedRewardState,
  );
  const [currentSlide, setCurrentSlide] = useState(0);

  const [equippedImage, setEquippedImage] = useState(String);
  const { user } = useAuth();

  const raw = localStorage.getItem(user!.uid)
  const parsed = JSON.parse(raw!);

  useEffect(() => {
    if (user) {
      setEquippedImage(getCurrentImageReward())
    }
  }, [getCurrentImageReward()])
  //funcion improvisada para inicializar la Imagen recompensa actual
  function getCurrentImageReward() {
    for (let i = 0; parsed?.Recompensas?.length > i; i++) {
      if (parsed?.Recompensas[i]?.Equipped === true) {
        return parsed.Recompensas[i].Image
      }
    }
  }

  //manejo de las recompensas cuando se seleciona uno nuevo. Se desequipa el anterior, se quipa el nuevo y se actualiza la base de datos
  const rewardHandle = async (newReward: any) => {
    const oldReward = itirateRewards()
    if (oldReward === undefined) {
      const newRew = parsed.Recompensas.map((rec: any) => {
        if (rec.Level === newReward.level) {
          rec.Equipped = true
        }
        return rec
      })
      UpdateEntryRewards(user!?.uid, newRew)
      return
    }
    if (oldReward.Level === newReward.level) {
      return
    }

    const newRew = parsed.Recompensas.map((rec: any) => {
      if (rec.Level === newReward.level) {
        rec.Equipped = true
      }
      if (rec.Level === oldReward.Level) {
        rec.Equipped = false
      }
      return rec
    })
    UpdateEntryRewards(user!?.uid, newRew)

  }
  //funcion simple de iteracion para rewardHandle
  function itirateRewards() {
    for (let i = 0; parsed.Recompensas.length > i; i++) {
      if (parsed.Recompensas[i].Equipped === true) {
        return parsed.Recompensas[i]
      }
    }
  }


  const sliderRef = useRef<Slider | null>(null);

  const firebaseTrackRewards = useMemo(
    () => normalizeRewardCollection(userData?.Recompensas),
    [userData?.Recompensas],
  );
  const firebaseInventoryRewards = useMemo(
    () => normalizeRewardCollection(userData?.Items),
    [userData?.Items],
  );
  const baseBattlePassRewards = firebaseTrackRewards;

  const ownedRewardIds = useMemo(() => {
    const ownedIds = new Set<string>(claimedRewards);

    firebaseInventoryRewards.forEach((reward) => ownedIds.add(reward.id));
    baseBattlePassRewards.forEach((reward) => {
      if (reward.claimed) {
        ownedIds.add(reward.id);
      }
    });

    return ownedIds;
  }, [baseBattlePassRewards, claimedRewards, firebaseInventoryRewards]);

  const battlePassRewards = useMemo(
    () =>
      baseBattlePassRewards.map((reward, index) => ({
        ...reward,
        level: reward.level || index + 1,
        claimed: reward.claimed || ownedRewardIds.has(reward.id),
      })),
    [baseBattlePassRewards, ownedRewardIds],
  );

  const inventoryRewards = useMemo(() => {
    const rewardMap = new Map<string, NormalizedReward>();

    firebaseInventoryRewards.forEach((reward) => {
      if (reward.type !== "placeholder") {
        rewardMap.set(reward.id, reward);
      }
    });

    battlePassRewards.forEach((reward) => {
      if (reward.type !== "placeholder" && ownedRewardIds.has(reward.id)) {
        rewardMap.set(reward.id, reward);
      }
    });

    return Array.from(rewardMap.values());
  }, [battlePassRewards, firebaseInventoryRewards, ownedRewardIds]);

  const rewardDisplayData = useMemo(() => {
    const rewardMap = new Map<string, NormalizedReward>();
    [...battlePassRewards, ...inventoryRewards].forEach((reward) => {
      rewardMap.set(reward.id, reward);
    });
    return Array.from(rewardMap.values());
  }, [battlePassRewards, inventoryRewards]);

  const battlePassXp = userData?.CurrentExp ?? 0;
  const levelThresholds = useMemo(
    () => buildLevelThresholds(battlePassRewards.length),
    [battlePassRewards.length],
  );
  const currentLevel = Math.min(
    userData?.Nivel ?? getCurrentLevel(battlePassXp, levelThresholds),
    battlePassRewards.length || 1,
  );

  const currentExpCap = () => {
    switch (userData?.Nivel) {
      case 1:
        return 100
      case 2:
        return 250
      case 3:
        return 500
      case 4:
        return 900
      case 5:
        return 1500
      case 6:
        return 2400
      case 7:
        return 3600
      case 8:
        return 5200
      case 9:
        return 7500
      case 10:
        return 10500
      case 11:
        return 14500
      case 12:
        return 17000
      case 13:
        return 18500
      case 14:
        return 19200
      case 15:
        return 19800
      case 16:
        return 20000
      case 17:
        return 28000
      case 18:
        return 40000
    }
  }
  const maxExp = currentExpCap();
  const currentExpProgress = userData?.CurrentExp! / maxExp! * 100
  const segmentProgress = getCurrentSegmentProgress(
    battlePassXp,
    levelThresholds,
    currentLevel,
  );

  const trackProgress = getTrackProgress(
    currentLevel,
    segmentProgress,
    battlePassRewards.length,
  );
  const rewardPages = chunkRewards(battlePassRewards, REWARDS_PER_PAGE);

  const handleEquipReward = (reward: NormalizedReward) => {
    if (!isRewardEquipable(reward)) {
      return;
    }

    if (reward.category === "badge") {
      const updatedState = { ...equippedRewards, badgeId: reward.id };
      setEquippedRewards(updatedState);
      saveEquippedRewardState(updatedState);
      return;
    }

    const accessoryItem = getAccessoryFromReward(reward);
    if (accessoryItem) {
      const updatedAvatar = {
        ...avatarConfig,
        accessories: {
          ...avatarConfig.accessories,
          [accessoryItem.type]: accessoryItem.id,
        },
      };

      setAvatarConfig(updatedAvatar);
      localStorage.setItem("userAvatar", JSON.stringify(updatedAvatar));
      return;
    }

    const updatedState = { ...equippedRewards, frameItemId: reward.id };
    setEquippedRewards(updatedState);
    saveEquippedRewardState(updatedState);

    //AAAAAAAAA
    rewardHandle(reward)

    setEquippedImage(reward.image!)
  };

  const handleClaimReward = (reward: NormalizedReward) => {
    if (
      reward.type === "placeholder" ||
      reward.level > currentLevel ||
      ownedRewardIds.has(reward.id)
    ) {
      return;
    }

    const updatedRewardIds = Array.from(
      new Set([...claimedRewards, reward.id]),
    );
    setClaimedRewards(updatedRewardIds);
    saveClaimedRewardIds(updatedRewardIds);

    if (reward.category === "badge" || reward.category === "avatar") {
      handleEquipReward(reward);
    }
  };

  const isRewardEquipped = (reward: NormalizedReward) => {
    if (reward.category === "badge") {
      return equippedRewards.badgeId === reward.id;
    }

    const accessoryItem = getAccessoryFromReward(reward);
    if (accessoryItem) {
      return avatarConfig.accessories[accessoryItem.type] === accessoryItem.id;
    }

    return equippedRewards.frameItemId === reward.id;
  };

  const renderRewardVisual = (reward: NormalizedReward, size: number) => {
    const visualValue = getRewardBadgeVisualValue(reward);

    if (!visualValue) {
      return null;
    }

    if (isImageSource(visualValue)) {
      return (
        <Box
          component="img"
          src={visualValue}
          alt={reward.name}
          sx={{ width: size, height: size, objectFit: "contain" }}
        />
      );
    }

    return (
      <Box
        component="span"
        sx={{
          fontSize: `${Math.max(22, size * 0.52)}px`,
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {visualValue}
      </Box>
    );
  };

  const panelCard = {
    backgroundColor: "var(--dashboard-panel-bg)",
    border: `3px solid ${dashboardPalette.panelBorder}`,
    borderRadius: "22px",
    boxShadow: "0 5px 0 rgba(62, 80, 147, 0.45)",
  };

  return (
    <DashboardLayout>
      <Box
        sx={{
          maxWidth: "1380px",
          margin: "0 auto",
          width: "100%",
          minHeight: "100%",
          px: { xs: 0, md: 1 },
          py: 1,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              width: "100%",
              background: "var(--dashboard-yellow)",
              color: "var(--dashboard-header-blue)",
              border: `3px solid ${dashboardPalette.accentYellowDark}`,
              borderRadius: "16px",
              minHeight: 58,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 0 rgba(211, 158, 0, 0.85)",
              px: 3,
              py: 1,
              textAlign: "center",
              mb: 2,
            }}
          >
            <Typography
              className="outlined-title-underlined"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "1.5rem", md: "2rem" },
                lineHeight: 1,
              }}
            >
              Progreso y Recompensas
            </Typography>
          </Box>

          <Box
            sx={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.68) 0%, rgba(236,240,248,0.76) 100%)",
              borderRadius: "18px",
              border: "2px solid rgba(110, 131, 203, 0.22)",
              p: { xs: 1.5, md: 2 },
            }}
          >
            {battlePassRewards.length === 0 ? (
              <Box
                sx={{
                  minHeight: 280,
                  borderRadius: "16px",
                  background: "rgba(255,255,255,0.55)",
                  border: "2px dashed rgba(110, 131, 203, 0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: 3,
                }}
              >
                <Typography
                  sx={{
                    color: "var(--dashboard-text)",
                    fontWeight: 700,
                    fontSize: "1rem",
                    maxWidth: 360,
                  }}
                >
                  No hay recompensas del pase cargadas desde Firebase para este
                  usuario.
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    flexWrap: "wrap",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "var(--dashboard-header-blue)",
                        fontWeight: 900,
                        fontSize: "1.35rem",
                        lineHeight: 1.1,
                      }}
                    >
                      Nivel {currentLevel}
                    </Typography>
                    <Typography
                      sx={{ color: "var(--dashboard-text)", mt: 0.5 }}
                    >
                      {battlePassXp} XP acumulado
                    </Typography>
                  </Box>

                  {rewardPages.length > 1 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        onClick={() => sliderRef.current?.slickPrev()}
                        sx={{
                          minWidth: 42,
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          border: `2px solid ${dashboardPalette.panelBorder}`,
                          background: "#fff",
                        }}
                      >
                        <ChevronLeftRounded />
                      </Button>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: "var(--dashboard-header-blue)",
                        }}
                      >
                        {currentSlide + 1} / {rewardPages.length}
                      </Typography>
                      <Button
                        onClick={() => sliderRef.current?.slickNext()}
                        sx={{
                          minWidth: 42,
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          border: `2px solid ${dashboardPalette.panelBorder}`,
                          background: "#fff",
                        }}
                      >
                        <ChevronRightRounded />
                      </Button>
                    </Box>
                  ) : null}
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    height: 10,
                    borderRadius: "999px",
                    background: "#c7d1e7",
                    overflow: "hidden",
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      width: `${currentExpProgress}%`,
                      height: "100%",
                      borderRadius: "999px",
                      background:
                        "linear-gradient(90deg, #f3a900 0%, #ffd347 70%, #8ad7ff 100%)",
                    }}
                  />
                </Box>

                <Slider
                  ref={sliderRef}
                  dots={false}
                  infinite={false}
                  slidesToShow={1}
                  slidesToScroll={1}
                  autoplay={false}
                  arrows={false}
                  adaptiveHeight={true}
                  afterChange={(slideIndex) => setCurrentSlide(slideIndex)}
                >
                  {rewardPages.map((pageRewards, pageIndex) => (
                    <div key={`battle-pass-page-${pageIndex}`}>
                      <Box
                        sx={{
                          overflowX: { xs: "auto", md: "visible" },
                          pb: { xs: 1, md: 0 },
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: `${Math.max(pageRewards.length, 5) * 150}px`,
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              px: 3.5,
                              pt: 2,
                              pb: 2.25,
                            }}
                          >
                            <Typography
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                color: "var(--dashboard-header-blue)",
                                fontWeight: 900,
                                fontSize: "1.05rem",
                                lineHeight: 1,
                              }}
                            >
                              Nivel
                            </Typography>
                            <Box
                              sx={{
                                position: "absolute",
                                left: 54,
                                right: 54,
                                top: 42,
                                height: 8,
                                borderRadius: "999px",
                                background: "#c7d1e7",
                              }}
                            />

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${pageRewards.length}, 1fr)`,
                                gap: 2,
                                position: "relative",
                                zIndex: 1,
                              }}
                            >
                              {pageRewards.map((reward) => {
                                const isUnlocked = reward.level <= currentLevel;
                                const isClaimed = ownedRewardIds.has(reward.id);

                                return (
                                  <Box
                                    key={reward.id}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 900,
                                        color: "var(--dashboard-header-blue)",
                                        border: `2px solid ${isUnlocked ? dashboardPalette.accentYellowDark : "#8ebfe5"}`,
                                        background: isUnlocked
                                          ? "var(--dashboard-yellow)"
                                          : "#7dd0ff",
                                        boxShadow: isUnlocked
                                          ? "0 3px 0 rgba(211, 158, 0, 0.95)"
                                          : "0 3px 0 rgba(71, 144, 199, 0.35)",
                                      }}
                                    >
                                      {reward.level}
                                    </Box>
                                    <Box
                                      sx={{
                                        width: 3,
                                        height: 18,
                                        borderRadius: "999px",
                                        background: isClaimed
                                          ? "var(--dashboard-teal)"
                                          : "#d9dfef",
                                      }}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: `repeat(${pageRewards.length}, minmax(140px, 1fr))`,
                              gap: 2,
                            }}
                          >
                            {pageRewards.map((reward) => {
                              const palette = getRewardPalette(reward);
                              const isUnlocked = reward.level <= currentLevel;
                              const isClaimed = ownedRewardIds.has(reward.id);
                              const isClaimable =
                                reward.type !== "placeholder" &&
                                isUnlocked &&
                                !isClaimed;

                              return (
                                <Card
                                  key={reward.id}
                                  sx={{
                                    minHeight: 224,
                                    borderRadius: "16px",
                                    border: `2px solid ${palette.border}`,
                                    background:
                                      reward.type === "placeholder"
                                        ? "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(246,247,251,0.9) 100%)"
                                        : palette.background,
                                    boxShadow:
                                      "0 8px 18px rgba(47, 43, 116, 0.08)",
                                  }}
                                >
                                  <CardContent
                                    sx={{
                                      p: 1.5,
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      textAlign: "center",
                                    }}
                                  >
                                    {reward.type === "placeholder" ? (
                                      <Box
                                        sx={{
                                          width: "100%",
                                          height: "100%",
                                          borderRadius: "12px",
                                          background:
                                            "linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(244,246,251,0.94) 100%)",
                                        }}
                                      />
                                    ) : (
                                      <>
                                        <Box
                                          sx={{
                                            width: 76,
                                            height: 76,
                                            borderRadius: "50%",
                                            background: palette.badgeBackground,
                                            color: palette.badgeColor,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border:
                                              "2px solid rgba(255,255,255,0.55)",
                                            mt: 0.5,
                                            overflow: "hidden",
                                          }}
                                        >
                                          {renderRewardVisual(reward, 54)}
                                        </Box>

                                        <Box sx={{ mt: 1.5 }}>
                                          <Typography
                                            sx={{
                                              color: palette.textColor,
                                              fontWeight: 800,
                                              fontSize: "0.92rem",
                                              lineHeight: 1.2,
                                              minHeight: 42,
                                            }}
                                          >
                                            {reward.name}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              color:
                                                reward.tone === "sky"
                                                  ? "rgba(255,255,255,0.9)"
                                                  : "rgba(61,61,61,0.78)",
                                              fontSize: "0.76rem",
                                              lineHeight: 1.35,
                                              minHeight: 52,
                                              mt: 0.8,
                                            }}
                                          >
                                            {reward.description}
                                          </Typography>
                                        </Box>

                                        {isClaimed ? (
                                          isRewardEquipable(reward) ? (
                                            <Button
                                              onClick={() =>
                                                handleEquipReward(reward)
                                              }
                                              sx={{
                                                mt: 1,
                                                minWidth: 110,
                                                borderRadius: "10px",
                                                background: isRewardEquipped(
                                                  reward,
                                                )
                                                  ? "var(--dashboard-teal)"
                                                  : "var(--dashboard-yellow)",
                                                color: isRewardEquipped(reward)
                                                  ? "#ffffff"
                                                  : "var(--dashboard-header-blue)",
                                                border: isRewardEquipped(reward)
                                                  ? "2px solid rgba(29, 144, 99, 0.9)"
                                                  : `2px solid ${dashboardPalette.accentYellowDark}`,
                                                boxShadow: isRewardEquipped(
                                                  reward,
                                                )
                                                  ? "0 3px 0 rgba(29, 144, 99, 0.55)"
                                                  : "0 3px 0 rgba(211, 158, 0, 0.95)",
                                                fontWeight: 900,
                                                "&:hover": {
                                                  background: isRewardEquipped(
                                                    reward,
                                                  )
                                                    ? "var(--dashboard-teal)"
                                                    : "var(--dashboard-yellow)",
                                                },
                                              }}
                                            >
                                              {isRewardEquipped(reward)
                                                ? "Equipado"
                                                : "Equipar"}
                                            </Button>
                                          ) : (
                                            <Chip
                                              icon={
                                                <CheckCircleRounded
                                                  sx={{
                                                    color: "inherit !important",
                                                  }}
                                                />
                                              }
                                              label="Obtenido"
                                              sx={{
                                                mt: 1,
                                                background:
                                                  "var(--dashboard-teal)",
                                                color: "#ffffff",
                                                fontWeight: 800,
                                              }}
                                            />
                                          )
                                        ) : isClaimable ? (
                                          <Button
                                            onClick={() =>
                                              handleClaimReward(reward)
                                            }
                                            sx={{
                                              mt: 1,
                                              minWidth: 110,
                                              borderRadius: "10px",
                                              background:
                                                "var(--dashboard-yellow)",
                                              color:
                                                "var(--dashboard-header-blue)",
                                              border: `2px solid ${dashboardPalette.accentYellowDark}`,
                                              boxShadow:
                                                "0 3px 0 rgba(211, 158, 0, 0.95)",
                                              fontWeight: 900,
                                              "&:hover": {
                                                background:
                                                  "var(--dashboard-yellow)",
                                              },
                                            }}
                                          >
                                            Obtener
                                          </Button>
                                        ) : (
                                          <Chip
                                            icon={
                                              <LockRounded
                                                sx={{
                                                  color: "inherit !important",
                                                }}
                                              />
                                            }
                                            label={`Nivel ${reward.level}`}
                                            sx={{
                                              mt: 1,
                                              background:
                                                "rgba(255,255,255,0.82)",
                                              color: "#65708b",
                                              fontWeight: 800,
                                            }}
                                          />
                                        )}
                                      </>
                                    )}
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                    </div>
                  ))}
                </Slider>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "320px minmax(0, 1fr)" },
            gap: 2.5,
            alignItems: "stretch",
          }}
        >
          <Card sx={{ ...panelCard, overflow: "hidden" }}>
            <CardContent
              sx={{
                p: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  height: 350,
                  m: 1,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <RewardedProfileDisplay
                  src={userData?.PortraitImg}
                  alt="Perfil"
                  fallbackSrc={avatarImage}
                  width={190}
                  height={190}
                  border={`4px solid ${dashboardPalette.headerBlue}`}
                  boxShadow="0 4px 0 rgba(47,43,116,0.25)"
                  backgroundColor="#fff4c4"
                  rewards={"rewardDisplayData"}
                  badgeSize={28}
                  overlaySize={36}
                />
                {equippedImage ?
                  <Box
                    sx={{
                      position: "absolute"
                    }}>
                    <Box
                      component="img"
                      src={equippedImage}
                      sx={{
                        width: 190,
                        height: 190,
                        position: "relative",
                        top: -23,
                      }}
                    />
                  </Box> : <></>}

              </Box>

              <Box
                sx={{
                  px: 2,
                  pb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    className="outlined-title-underlined"
                    sx={{ fontWeight: 900, fontSize: "2rem" }}
                  >
                    {userData?.Nombre || "Usuario"}
                  </Typography>
                  <Typography
                    sx={{
                      color: "var(--dashboard-text)",
                      fontSize: "1.4rem",
                    }}
                  >
                    Nivel {userData?.Nivel ?? currentLevel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    background: "var(--dashboard-yellow)",
                    color: "var(--dashboard-header-blue)",
                    borderRadius: "999px",
                    px: 1.6,
                    py: 0.55,
                    border: `2px solid ${dashboardPalette.accentYellowDark}`,
                    fontWeight: 900,
                    fontSize: "0.92rem",
                    lineHeight: 1,
                  }}
                >
                  Nivel {currentLevel}
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ ...panelCard, overflow: "hidden" }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Typography
                className="outlined-title"
                sx={{
                  fontSize: "1.35rem",
                  fontWeight: 900,
                  mb: 1.8,
                  ml: 0.5,
                  lineHeight: 1,
                }}
              >
                Inventario
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {inventoryRewards.length === 0 ? (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      minHeight: 180,
                      borderRadius: "16px",
                      background: "rgba(255,255,255,0.58)",
                      border: "2px solid rgba(223, 228, 239, 0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      px: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "var(--dashboard-text)",
                        fontWeight: 700,
                        fontSize: "1rem",
                      }}
                    >
                      Aún no tienes recompensas en el inventario.
                    </Typography>
                  </Box>
                ) : (
                  inventoryRewards.map((reward) => {
                    const palette = getRewardPalette(reward);
                    const isEquipped = isRewardEquipped(reward);
                    const canEquip = isRewardEquipable(reward);

                    return (
                      <Card
                        key={reward.id}
                        sx={{
                          minHeight: 224,
                          borderRadius: "16px",
                          border: `2px solid ${palette.border}`,
                          background: "rgba(255,255,255,0.9)",
                          boxShadow: "0 8px 18px rgba(47, 43, 116, 0.08)",
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 1.5,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: 76,
                              height: 76,
                              borderRadius: "50%",
                              background: palette.badgeBackground,
                              color: palette.badgeColor,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `2px solid ${palette.border}`,
                              overflow: "hidden",
                            }}
                          >
                            {renderRewardVisual(reward, 54)}
                          </Box>

                          <Typography
                            sx={{
                              mt: 1.5,
                              color: "var(--dashboard-header-blue)",
                              fontWeight: 800,
                              fontSize: "0.92rem",
                              lineHeight: 1.25,
                            }}
                          >
                            {reward.name}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.8,
                              color: "var(--dashboard-text)",
                              fontSize: "0.76rem",
                              lineHeight: 1.35,
                              minHeight: 40,
                            }}
                          >
                            {reward.description}
                          </Typography>

                          {canEquip ? (
                            <Button
                              onClick={() => handleEquipReward(reward)}
                              sx={{
                                mt: 1.5,
                                minWidth: 110,
                                borderRadius: "10px",
                                background: isEquipped
                                  ? "var(--dashboard-teal)"
                                  : "var(--dashboard-yellow)",
                                color: isEquipped
                                  ? "#ffffff"
                                  : "var(--dashboard-header-blue)",
                                border: isEquipped
                                  ? "2px solid rgba(29, 144, 99, 0.9)"
                                  : `2px solid ${dashboardPalette.accentYellowDark}`,
                                boxShadow: isEquipped
                                  ? "0 3px 0 rgba(29, 144, 99, 0.55)"
                                  : "0 3px 0 rgba(211, 158, 0, 0.95)",
                                fontWeight: 900,
                                "&:hover": {
                                  background: isEquipped
                                    ? "var(--dashboard-teal)"
                                    : "var(--dashboard-yellow)",
                                },
                              }}
                            >
                              {isEquipped ? "Equipado" : "Equipar"}
                            </Button>
                          ) : (
                            <Chip
                              label="Obtenido"
                              sx={{
                                mt: 1.5,
                                background: "#eaf9ef",
                                color: "#2e7d53",
                                fontWeight: 800,
                              }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default Redeem;
